import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Button, Typography } from "@mui/material";
import { FC, memo, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import RequiredActivities from "web/Pages/Checkout/SubscriptionMUI/Success/RequiredActivities/requiredActivities";

import ErrorComponent from "web/Layout/Common/ErrorComponent";
import Loading from "web/Layout/Common/Loading";
import GtmEvent from "web/Layout/Gtm/GtmEvent";
import __ from "web/Layout/Translations";

import withScrollToTop from "web/Components/Common/withScrollToTop/withScrollToTop";

import {
  hasBenefitsWithPdfForms,
  hasBenefitsWithReceiverForms,
} from "web/utils/page/product/subscription/hasForms";
import setBenefitGroupTypeParam from "web/utils/page/product/subscription/setBenefitGroupTypeParam";
import getPageReferrer from "web/utils/system/GTM/getPageReferrer";
import getSearchParameter from "web/utils/system/url/getSearchParameter";

import {
  BENEFIT_GROUP_TYPE,
  NOT_ACTIVE_STATUSES,
} from "web/constants/benefits";
import urls from "web/constants/urls";

import {
  ISubscriptionTotals,
  ITotalsCalculated,
  SubscriptionCheckoutSteps,
  SubscriptionGroups,
} from "web/types/Subscription";
import { isFetchBaseQueryError } from "web/types/Utils";

import { setBenefitGroupType } from "web/features/subscriptionQuote/subscriptionQuoteSlice";
import { useGetCurrentUserSubscriptionQuery } from "web/features/subscriptions/subscriptionsApiSlice";

import AsideMUI from "../Aside";
import AdditionalCarousel from "./AdditionalCarousel/additionalCarousel";

const CheckoutSubscriptionSuccess: FC = memo(() => {
  let summary;

  const dispatch = useDispatch();
  const history = useHistory();
  const { search, pathname } = useLocation();
  const chosenBenefitGroupType = getSearchParameter({
    name: BENEFIT_GROUP_TYPE,
    search,
  }) as SubscriptionGroups;

  dispatch(
    setBenefitGroupType({
      benefitGroupType: chosenBenefitGroupType,
    })
  );

  const { data, isLoading, error } = useGetCurrentUserSubscriptionQuery(
    undefined,
    {
      selectFromResult: ({ data, isLoading, isFetching, error }) => ({
        data: data?.find(
          (group) => group.benefitGroupType === Number(chosenBenefitGroupType)
        ),
        isLoading,
        isFetching,
        error,
      }),
      // TODO: Check if invalidating tag could be used instead of hard refetch - todo after implementing RTK in configurator
      refetchOnMountOrArgChange: true,
    }
  );

  if (isFetchBaseQueryError(error) && error.status === 404) {
    history.push({
      pathname: urls.checkoutSubscriptionConfiguration,
      search: setBenefitGroupTypeParam(chosenBenefitGroupType),
    });
    return null;
  }

  const gtmOptions = useMemo(
    () => ({
      cgroup1: "Koszyk",
      cgroup2: "Zapisano świadczenia",
      page_referrer: getPageReferrer({ search, pathname }),
    }),
    [search, pathname, getPageReferrer]
  );

  switch (true) {
    case !!error: {
      console.error(error);
      summary = <ErrorComponent />;
      break;
    }
    case !!isLoading || !data: {
      summary = <Loading />;
      break;
    }
    default: {
      const hasReceiverFormsToFill = hasBenefitsWithReceiverForms(
        data || null,
        true
      );
      const hasPdfBarcodes = hasBenefitsWithPdfForms(data || null, true);
      const hasAnyFormsToFill = hasReceiverFormsToFill || hasPdfBarcodes;
      const isAnyBenefitPending = data?.items?.some((ele) =>
        NOT_ACTIVE_STATUSES.includes(ele.status)
      );
      const showOfflineDeclarations =
        isAnyBenefitPending && data?.agreementsPdfUrl;

      // Dodanie danych pointsBank, analogicznie jak na krokach konfiguratora
      const dataTotals = data?.totals || {};
      const prepTotals = {
        ...dataTotals,
        pointsBanks: data?.pointsBanks || [],
      };

      summary = (
        <>
          <Button
            variant="contained"
            color="primary"
            size="large"
            href={urls.customerSubscriptions}
            data-t1="proceedToMySubscriptions"
            sx={{
              mt: 2,
              mx: { xs: 2, lg: 0 },
              "&:hover": {
                color: "#FFF",
              },
            }}
          >
            {__("Przejdź do Moich abonamentów")}
            <ChevronRightIcon sx={{ ml: 1 }} />
          </Button>
          <section>
            {(hasAnyFormsToFill || showOfflineDeclarations) && data && (
              <RequiredActivities
                hasReceiverFormsToFill={hasReceiverFormsToFill}
                hasPdfBarcodes={hasPdfBarcodes}
                showOfflineDeclarations={!!showOfflineDeclarations}
                data={data}
              />
            )}
          </section>

          <Box
            sx={{
              width: {
                lg:
                  Boolean(prepTotals?.totalPriceSummary) &&
                  prepTotals?.totalPriceSummary !== "0.00"
                    ? "100%"
                    : "50%",
              },
              border: "1px solid #E0E0E0",
              borderRadius: 2,
              p: 3,
              mt: { xs: 6, lg: 4 },
              mx: { xs: 2, lg: 0 },
              mb: { xs: 3, lg: 0 },
            }}
          >
            <AsideMUI
              totals={prepTotals as ISubscriptionTotals | ITotalsCalculated}
              group={chosenBenefitGroupType}
              step={SubscriptionCheckoutSteps.SUCCESS}
              totalsIsLoading={isLoading}
              totalsError=""
            />
          </Box>
        </>
      );
    }
  }

  return (
    <>
      <Box
        sx={{
          margin: "auto",
          mt: 6,
          maxWidth: { xs: "auto", lg: 1192 },
        }}
      >
        <Box sx={{ mx: "auto" }}>
          <Typography variant="h1" sx={{ mx: { xs: 2, lg: 0 } }}>
            {__("Dziękujemy")}
          </Typography>
          <Typography variant="h2" sx={{ mt: 0.5, mx: { xs: 2, lg: 0 } }}>
            {__("za wybór abonamentów")}
          </Typography>
          {summary}
        </Box>
        <AdditionalCarousel />
      </Box>
      <GtmEvent options={gtmOptions} withUrl />
    </>
  );
});

export default withScrollToTop(CheckoutSubscriptionSuccess);
